import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Space, Tag, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import AmazonWebServicesContext from '@totem/components/administration/amazonWebServices/AmazonWebServicesContext';
import AwsInstanceActionMenu from '@totem/components/administration/amazonWebServices/AwsInstanceActionMenu';
import InstanceCostTable from '@totem/components/administration/amazonWebServices/InstanceCostTable';
import InstanceSalesOrdersTable from '@totem/components/administration/amazonWebServices/InstanceSalesOrdersTable';
import {
  AWS_INSTANCE_STATUS_OPTIONS,
  AwsInstanceRecord,
} from '@totem/components/administration/amazonWebServices/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { DateStringToLocal } from '@totem/utilities/dateUtilities';

// Sentinel used throughout the app for "no reference assigned" — a fresh
// instance's hierarchyReference comes back with org/building names set to
// this same string, not an empty one (see mongo-client-library's
// GetEmptyHierarchyReference), so it has to be checked for explicitly.
const EMPTY_ID = '000000000000000000000000';

const stateColor = (state: string) => {
  switch (state) {
    case 'running':
      return 'green';
    case 'stopped':
    case 'terminated':
      return 'default';
    case 'pending':
    case 'stopping':
      return 'gold';
    default:
      return 'blue';
  }
};

export const statusColor = (status: string) => {
  switch (status) {
    case 'Asset Inventory':
    case 'Remote Access':
    case 'Optimize':
      return 'green';
    case 'Development':
      return 'blue';
    case 'Overhead':
      return 'gold';
    case 'Decommissioning':
      return 'red';
    default:
      return 'default';
  }
};

type Props = {
  // Applied on top of the context's data, e.g. to scope this table down to
  // instances with no sales order assigned (see the "AWS" tab on the Sales
  // Order Commissioning report).
  filterPredicate?: (record: AwsInstanceRecord) => boolean;
};

const organizationName = (record: AwsInstanceRecord) => {
  const organization = record.hierarchyReference?.organization;
  return !organization?.id || organization.id === EMPTY_ID
    ? 'Unassigned'
    : organization.name;
};

const buildingName = (record: AwsInstanceRecord) => {
  const building = record.hierarchyReference?.building;
  return !building?.id || building.id === EMPTY_ID
    ? 'Unassigned'
    : building.name;
};

const deviceName = (record: AwsInstanceRecord) => {
  const device = record.item.device;
  return !device?.id || device.id === EMPTY_ID ? 'Unassigned' : device.name;
};

const filterOptionsFrom = (values: string[]) =>
  Array.from(new Set(values))
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({ text: value, value }));

const AmazonWebServicesTable = ({ filterPredicate }: Props = {}) => {
  const { loading, data } = useContext(AmazonWebServicesContext);
  const [pageSize, setPageSize] = useState<number>(25);
  const filteredData = filterPredicate ? data.filter(filterPredicate) : data;

  const organizationOptions = filterOptionsFrom(
    filteredData.map(organizationName),
  );
  const buildingOptions = filterOptionsFrom(filteredData.map(buildingName));

  const columns: ColumnProps<AwsInstanceRecord>[] = [
    {
      title: 'Name',
      key: 'name',
      sorter: (left, right) => left.name.localeCompare(right.name),
      defaultSortOrder: 'ascend',
      render: (_, record) => (
        <span>{record.name || record.item.instance.instanceId}</span>
      ),
    },
    {
      title: 'Instance ID',
      key: 'instanceId',
      render: (_, record) => <code>{record.item.instance.instanceId}</code>,
    },
    {
      title: 'Type',
      key: 'instanceType',
      sorter: (left, right) =>
        left.item.instance.instanceType.localeCompare(
          right.item.instance.instanceType,
        ),
      render: (_, record) => <span>{record.item.instance.instanceType}</span>,
    },
    {
      title: 'State',
      key: 'state',
      filters: [
        { text: 'running', value: 'running' },
        { text: 'stopped', value: 'stopped' },
        { text: 'terminated', value: 'terminated' },
        { text: 'pending', value: 'pending' },
        { text: 'stopping', value: 'stopping' },
      ],
      onFilter: (value, record) => record.item.instance.state === value,
      render: (_, record) => (
        <Tag color={stateColor(record.item.instance.state)}>
          {record.item.instance.state}
        </Tag>
      ),
    },
    {
      title: 'Organization',
      key: 'organization',
      filters: organizationOptions,
      onFilter: (value, record) => organizationName(record) === value,
      sorter: (left, right) =>
        organizationName(left).localeCompare(organizationName(right)),
      render: (_, record) => {
        const name = organizationName(record);

        return name === 'Unassigned' ? (
          <span style={{ color: 'var(--ant-color-text-tertiary)' }}>
            {name}
          </span>
        ) : (
          <span>{name}</span>
        );
      },
    },
    {
      title: 'Building',
      key: 'building',
      filters: buildingOptions,
      onFilter: (value, record) => buildingName(record) === value,
      sorter: (left, right) =>
        buildingName(left).localeCompare(buildingName(right)),
      render: (_, record) => {
        const name = buildingName(record);

        return name === 'Unassigned' ? (
          <span style={{ color: 'var(--ant-color-text-tertiary)' }}>
            {name}
          </span>
        ) : (
          <span>{name}</span>
        );
      },
    },
    {
      title: 'Device',
      key: 'device',
      filters: [
        { text: 'Assigned', value: 'assigned' },
        { text: 'Unassigned', value: 'unassigned' },
      ],
      onFilter: (value, record) =>
        value === 'assigned'
          ? deviceName(record) !== 'Unassigned'
          : deviceName(record) === 'Unassigned',
      sorter: (left, right) =>
        deviceName(left).localeCompare(deviceName(right)),
      render: (_, record) => {
        const name = deviceName(record);

        return name === 'Unassigned' ? (
          <span style={{ color: 'var(--ant-color-text-tertiary)' }}>
            {name}
          </span>
        ) : (
          <Link to={`/dashboard/devices/${record.item.device.id}`}>{name}</Link>
        );
      },
    },
    {
      title: 'Private IP',
      key: 'privateIp',
      render: (_, record) => <span>{record.item.instance.privateIp}</span>,
    },
    {
      title: 'Sales Orders',
      key: 'salesOrders',
      filters: [
        { text: 'Assigned', value: 'assigned' },
        { text: 'Unassigned', value: 'unassigned' },
      ],
      onFilter: (value, record) =>
        value === 'assigned'
          ? record.item.salesOrders.length > 0
          : record.item.salesOrders.length === 0,
      render: (_, record) => {
        const salesOrders = record.item.salesOrders ?? [];

        if (salesOrders.length === 0) {
          return <Tag color="red">Unassigned</Tag>;
        }

        const orderNumbers = Array.from(
          new Set(salesOrders.map((line) => line.salesOrderNumber)),
        );

        return (
          <Space size={4} wrap>
            {orderNumbers.map((orderNumber) => {
              const services = salesOrders
                .filter((line) => line.salesOrderNumber === orderNumber)
                .map((line) => line.service)
                .join(', ');

              return (
                <Link
                  key={orderNumber}
                  to={`/dashboard/salesOrder/${encodeURIComponent(orderNumber)}`}
                  title={services}
                >
                  <Tag color="blue">{orderNumber}</Tag>
                </Link>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      filters: AWS_INSTANCE_STATUS_OPTIONS.map((option) => ({
        text: option,
        value: option,
      })),
      onFilter: (value, record) => record.status === value,
      render: (_, record) => (
        <Tag color={statusColor(record.status)}>{record.status}</Tag>
      ),
    },
    {
      title: 'Last Sync',
      key: 'lastSyncTime',
      sorter: (left, right) =>
        new Date(left.lastSyncTime).getTime() -
        new Date(right.lastSyncTime).getTime(),
      render: (_, record) => (
        <span>{DateStringToLocal(record.lastSyncTime)}</span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_, record) => <AwsInstanceActionMenu instance={record} />,
    },
  ];

  return (
    <IBTable
      rowKey={(record) => record.id}
      columns={columns}
      dataSource={filteredData}
      loading={loading}
      pagination={{
        pageSize,
        showSizeChanger: true,
        onShowSizeChange: (_, size) => setPageSize(size),
      }}
      expandable={{
        expandedRowRender: (record) => (
          <>
            <Typography.Title level={5}>Sales Orders</Typography.Title>
            <InstanceSalesOrdersTable
              salesOrders={record.item.salesOrders ?? []}
            />
            <Typography.Title level={5} style={{ marginTop: 16 }}>
              Cost
            </Typography.Title>
            <InstanceCostTable instanceId={record.item.instance.instanceId} />
          </>
        ),
      }}
    />
  );
};

export default AmazonWebServicesTable;
